import {FC, ReactNode, useRef, useState} from 'react';
import CountryConfiguration from './CountryConfiguration';
import {Alpha3Code} from 'i18n-iso-countries';
import {useTranslation} from 'react-i18next';
import ViewTitle from 'viewComponents/ViewTitle/ViewTitle';
import TabView, {TabProps} from 'components/TabView/TabView';
import LeaveParamsCreateCountryForm from 'viewComponents/LeaveParamsCreateCountryForm/LeaveParamsCreateCountryForm';
import AddIcon from '@mui/icons-material/Add';
import GqlQueryController from 'controllers/GqlQueryController/GqlQueryController';
import {GET_MANAGEMENT_CONFIGURATIONS} from 'graphql/absences/Querys';
import {ParamsConfigurationProps} from 'models/configuration';
import DisableCountryModal from 'viewComponents/DisableCountryModal/DisableCountryModal';
import EnableCountryModal from 'viewComponents/EnableCountryModal/EnableCountryModal';
import CountryReassignmentModal from 'viewComponents/CountryReassignmentModal/CountryReassignmentModal';
import countries from 'i18n-iso-countries';

export interface ParamsConfigurationProps2 {
  /** Optional content rendered between the page title and the country tabs. */
  beforeContent?: ReactNode;
  /**
   * Controls which section is displayed.
   * - 'country' (default): renders the existing country-level configuration tabs.
   * - 'palowan': reserved for the per-palowan view introduced by US-02.01;
   *   the parent container is responsible for injecting that content via children
   *   or a sibling component.
   */
  activeTab?: 'country' | 'palowan';
}

/**
 * Renders the country tabs for management configuration and loads the admin-only
 * vacation regulation dataset required by the parameters module.
 *
 * @param beforeContent - Optional content to render between the page title and the tabs.
 * @param activeTab - Which top-level section to display ('country' by default).
 * @returns The management parameters view.
 */
const ParamsConfiguration: FC<ParamsConfigurationProps2> = ({
  beforeContent,
  activeTab = 'country',
}) => {
  const {t} = useTranslation();

  // Holds the fetch callback exposed by GqlQueryController's render prop so that
  // the modals (rendered outside the render prop scope) can trigger a re-fetch.
  // client.refetchQueries does NOT work here because GqlQueryController uses
  // useLazyQuery with local React state and ignores Apollo cache updates.
  const refetchConfigurationsRef = useRef<(() => Promise<void>) | null>(null);

  const [disableModalCountry, setDisableModalCountry] = useState<string | null>(
    null
  );
  const [enableModalCountry, setEnableModalCountry] = useState<string | null>(
    null
  );
  const [reassignmentModalCountry, setReassignmentModalCountry] = useState<
    string | null
  >(null);

  return (
    <div className="Dashboard w-full flex flex-col flex-wrap pt-12">
      <div className="flex mb-6">
        <ViewTitle disableBackButton>
          {t('leaves.ParamsConfiguration.title')}
        </ViewTitle>
      </div>
      {activeTab === 'country' && (
        <>
          {beforeContent}
          <GqlQueryController
            initialState={[] as ParamsConfigurationProps[]}
            QUERY={GET_MANAGEMENT_CONFIGURATIONS}
            variables={{includeInactive: true}}
            rootField={'getConfigurations'}
          >
            {({data: configurations, fetch}) => {
              // Capture the fetch callback so modals rendered outside this render
              // prop can trigger a re-fetch (useRef avoids stale closure issues).
              refetchConfigurationsRef.current = fetch;

              const tabs: TabProps[] = configurations.map(
                (configuration: ParamsConfigurationProps) => ({
                  label: configuration.country,
                  props:
                    configuration.isActive === false
                      ? {sx: {color: 'grey.500'}}
                      : undefined,
                  view: (
                    <CountryConfiguration
                      country={configuration.country as Alpha3Code}
                      holidays={configuration.holidays}
                      inabilities={configuration.inabilities}
                      vacations={configuration.vacations}
                      vacationRegulations={configuration.vacationRegulations}
                      monetizationRules={configuration.monetizationRules}
                      fetch={fetch}
                      isActive={configuration.isActive !== false}
                      onDisable={() =>
                        setDisableModalCountry(configuration.country)
                      }
                      onEnable={() =>
                        setEnableModalCountry(configuration.country)
                      }
                    />
                  ),
                })
              );

              tabs.push({
                label: '',
                props: {
                  icon: <AddIcon />,
                  iconPosition: 'start',
                },
                view: <LeaveParamsCreateCountryForm onSubmit={fetch} />,
              });

              return <TabView tabs={tabs} />;
            }}
          </GqlQueryController>

          <DisableCountryModal
            open={disableModalCountry !== null}
            countryCode={disableModalCountry ?? ''}
            countryName={
              disableModalCountry
                ? countries.getName(disableModalCountry as Alpha3Code, 'es') ??
                  disableModalCountry
                : ''
            }
            onClose={() => setDisableModalCountry(null)}
            onSuccess={count => {
              // Invoke the GqlQueryController fetch directly — client.refetchQueries
              // does not work here because the controller uses a local React state cache.
              refetchConfigurationsRef.current?.();
              if (count > 0) {
                setReassignmentModalCountry(disableModalCountry);
              }
              setDisableModalCountry(null);
            }}
          />

          <EnableCountryModal
            open={enableModalCountry !== null}
            countryCode={enableModalCountry ?? ''}
            countryName={
              enableModalCountry
                ? countries.getName(enableModalCountry as Alpha3Code, 'es') ??
                  enableModalCountry
                : ''
            }
            onClose={() => setEnableModalCountry(null)}
            onSuccess={() => {
              refetchConfigurationsRef.current?.();
              setEnableModalCountry(null);
            }}
          />

          {reassignmentModalCountry !== null && (
            <CountryReassignmentModal
              open={reassignmentModalCountry !== null}
              countryCode={reassignmentModalCountry}
              onSuccess={() => {
                refetchConfigurationsRef.current?.();
                setReassignmentModalCountry(null);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ParamsConfiguration;
